import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { GET_LIST_PRODUCT_QUERY_KEY } from '@/features/products/api/get-list-product.api';
import { publishProductMutationOptions } from '@/features/products/api/publish-product.api';

export interface PublishProductDialogProps extends BaseDialogProps {
  productId: string;
  productTitle: string;
}

export default function PublishProductDialog({
  dialog,
  productId,
  productTitle,
}: PublishProductDialogProps) {
  const { closePublishProductDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const publishProductMutation = useMutation({
    ...publishProductMutationOptions(),
    onSuccess: () => {
      toast.success('Produk berhasil dipublikasikan');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_PRODUCT_QUERY_KEY] });
      closePublishProductDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal mempublikasikan produk';
      toast.error(message);
    },
  });

  const isPending = publishProductMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closePublishProductDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Publikasikan produk ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Produk "{productTitle}" akan dipublikasikan dan dapat dilihat oleh
            publik.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() =>
              publishProductMutation.mutate({ product_id: productId })
            }
          >
            {isPending ? 'Memproses...' : 'Publikasi'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
